"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.convertExternalComponentToFlatSignalTie = convertExternalComponentToFlatSignalTie;
exports.convertFlatSignalTieToExternalComponent = convertFlatSignalTieToExternalComponent;
exports.flatTieToRowValues = flatTieToRowValues;
exports.signalTieRowValuesToFlatTie = signalTieRowValuesToFlatTie;
exports.applySignalTieRowsToSignals = applySignalTieRowsToSignals;
exports.splitExternalComponentByConnection = splitExternalComponentByConnection;
exports.normalizeSignalTies = normalizeSignalTies;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const signal_tie_types_1 = require("./signal-tie-types");
const tie_component_value_1 = require("./tie-component-value");
const POLARITY_TO_TARGET_MODEL_SIGNAL_ID = {
    [signal_tie_types_1.SignalTiePolarity.PositiveOnSignal]: '-',
    [signal_tie_types_1.SignalTiePolarity.PositiveOnTarget]: '+',
};
const TARGET_MODEL_SIGNAL_ID_TO_POLARITY = {
    '-': signal_tie_types_1.SignalTiePolarity.PositiveOnSignal,
    '+': signal_tie_types_1.SignalTiePolarity.PositiveOnTarget,
};
function convertRangeBoundaryToFlatSignalTie(boundary, componentType, toSignalId) {
    return {
        toSignalId,
        connectionType: signal_tie_types_1.SignalTieConnectionType.ThroughComponent,
        componentType,
        componentValue: {
            type: 'range',
            min: boundary.minVal,
            max: boundary.maxVal,
        },
    };
}
function convertExternalComponentToFlatSignalTie(component, signals, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
    if (component.rootComponentSignalId && !component.model) {
        const matchingSignal = signals?.find((s) => String(s.id) === component.rootComponentSignalId);
        if (signals && !matchingSignal) {
            logger.warn('ConvertExternalComponentToFlatSignalTie_DirectTieSignalNotFound', {
                rootComponentSignalId: component.rootComponentSignalId,
            });
            return null;
        }
        return {
            toSignalId: component.rootComponentSignalId,
            connectionType: signal_tie_types_1.SignalTieConnectionType.Direct,
            componentType: null,
            componentValue: null,
        };
    }
    if (component.model && component.withConnections) {
        const targetConnection = component.withConnections[0];
        const targetSignalRef = targetConnection?.components?.[0]?.rootComponentSignalId;
        if (!targetSignalRef) {
            logger.warn('ConvertExternalComponentToFlatSignalTie_MissingTargetSignal', { component });
            return null;
        }
        const matchingSignal = signals?.find((s) => String(s.id) === targetSignalRef);
        if (signals && !matchingSignal) {
            logger.warn('ConvertExternalComponentToFlatSignalTie_IndirectTieSignalNotFound', {
                targetSignalRef,
            });
            return null;
        }
        const componentType = signal_tie_types_1.MODEL_TO_TIE_COMPONENT_TYPE[component.model];
        if (!componentType) {
            logger.warn('ConvertExternalComponentToFlatSignalTie_UnsupportedModel', {
                model: component.model,
            });
            return null;
        }
        const polarity = (0, signal_tie_types_1.isPolarizedTieComponentType)(componentType) && targetConnection?.modelSignalId !== undefined ?
            TARGET_MODEL_SIGNAL_ID_TO_POLARITY[targetConnection.modelSignalId]
            : undefined;
        const tie = convertComponentValueToFlatSignalTie(component, componentType, targetSignalRef, logger);
        return polarity === undefined ? tie : { ...tie, polarity };
    }
    logger.warn('ConvertExternalComponentToFlatSignalTie_UnsupportedFormat', { component });
    return null;
}
function convertComponentValueToFlatSignalTie(component, componentType, targetSignalRef, logger) {
    const boundary = component.value;
    if (boundary === undefined) {
        return {
            toSignalId: targetSignalRef,
            connectionType: signal_tie_types_1.SignalTieConnectionType.ThroughComponent,
            componentType,
            componentValue: null,
        };
    }
    switch (boundary.type) {
        case 'absolute': {
            if (boundary.variance) {
                const units = signal_tie_types_1.TIE_COMPONENT_TYPE_TO_UNITS[componentType];
                const varianceProps = sigma_engine_shared_1.VarianceChecker.valueVarianceToProperties({ variance: boundary.variance }, units);
                if (!varianceProps) {
                    logger.warn('ConvertExternalComponentToFlatSignalTie_AbsoluteBoundaryVarianceParseFailure', {
                        boundary,
                        componentName: component.name,
                    });
                }
                else {
                    const isRelative = varianceProps.units.short === sigma_engine_shared_1.CommonParameterUnits.percentage.short;
                    return {
                        toSignalId: targetSignalRef,
                        connectionType: signal_tie_types_1.SignalTieConnectionType.ThroughComponent,
                        componentType,
                        componentValue: {
                            type: 'absolute',
                            value: boundary.value,
                            variance: varianceProps.value,
                            varianceType: isRelative ? signal_tie_types_1.SignalTieVarianceType.Relative : signal_tie_types_1.SignalTieVarianceType.Absolute,
                        },
                    };
                }
            }
            return {
                toSignalId: targetSignalRef,
                connectionType: signal_tie_types_1.SignalTieConnectionType.ThroughComponent,
                componentType,
                componentValue: {
                    type: 'absolute',
                    value: boundary.value,
                    variance: null,
                    varianceType: null,
                },
            };
        }
        case 'range':
            return convertRangeBoundaryToFlatSignalTie(boundary, componentType, targetSignalRef);
        case 'minimum':
            return {
                toSignalId: targetSignalRef,
                connectionType: signal_tie_types_1.SignalTieConnectionType.ThroughComponent,
                componentType,
                componentValue: {
                    type: 'range',
                    min: boundary.value,
                    max: null,
                },
            };
        case 'maximum':
            return {
                toSignalId: targetSignalRef,
                connectionType: signal_tie_types_1.SignalTieConnectionType.ThroughComponent,
                componentType,
                componentValue: {
                    type: 'range',
                    min: null,
                    max: boundary.value,
                },
            };
    }
}
function convertFlatSignalTieToExternalComponent(flatTie, signals, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
    const getSignalDisplayName = (signalId) => {
        if (signals) {
            const signal = signals.find((s) => s.id === signalId);
            if (signal) {
                return signal.name;
            }
        }
        return String(signalId);
    };
    if (flatTie.connectionType === signal_tie_types_1.SignalTieConnectionType.Direct) {
        const signalName = getSignalDisplayName(flatTie.toSignalId);
        return {
            name: `Direct connection to signal ${signalName}`,
            rootComponentSignalId: flatTie.toSignalId,
        };
    }
    if (flatTie.connectionType === signal_tie_types_1.SignalTieConnectionType.ThroughComponent) {
        if (!flatTie.componentType) {
            logger.warn('ConvertFlatSignalTieToExternalComponent_MissingComponentData', { flatTie });
            throw new Error('Component type is required for ThroughComponent ties');
        }
        const model = signal_tie_types_1.TIE_COMPONENT_TYPE_TO_MODEL[flatTie.componentType];
        const characteristic = signal_tie_types_1.TIE_COMPONENT_TYPE_TO_CHARACTERISTIC[flatTie.componentType];
        const signalName = getSignalDisplayName(flatTie.toSignalId);
        const targetModelSignalId = flatTie.polarity === undefined ? undefined : POLARITY_TO_TARGET_MODEL_SIGNAL_ID[flatTie.polarity];
        const targetConnection = {
            name: `Tie to ${signalName}`,
            ...(targetModelSignalId !== undefined && { modelSignalId: targetModelSignalId }),
            components: [
                {
                    name: `Tie to ${signalName}`,
                    rootComponentSignalId: flatTie.toSignalId,
                },
            ],
        };
        const componentValue = flatTie.componentValue;
        if (componentValue === null) {
            return {
                name: `Connection through ${model}`,
                model,
                withConnections: [targetConnection],
            };
        }
        else if (componentValue.type === 'absolute') {
            let varianceString;
            if (componentValue.variance !== null && componentValue.varianceType !== null) {
                const units = signal_tie_types_1.TIE_COMPONENT_TYPE_TO_UNITS[flatTie.componentType];
                const varianceUnits = componentValue.varianceType === signal_tie_types_1.SignalTieVarianceType.Relative ? sigma_engine_shared_1.CommonParameterUnits.percentage : units;
                varianceString = sigma_engine_shared_1.VarianceChecker.buildValueVariance(componentValue.variance, varianceUnits, false).variance;
            }
            return {
                name: `Connection through ${model}`,
                model,
                value: {
                    type: 'absolute',
                    characteristic,
                    value: componentValue.value,
                    ...(varianceString && { variance: varianceString }),
                },
                withConnections: [targetConnection],
            };
        }
        if (componentValue.type === 'range') {
            if (componentValue.min === null && componentValue.max === null) {
                logger.warn('ConvertFlatSignalTieToExternalComponent_RangeWithNoBounds', { flatTie });
                throw new Error('Range component value must have at least one bound (min or max)');
            }
            let value;
            if (componentValue.min !== null && componentValue.max !== null) {
                value = {
                    type: 'range',
                    characteristic,
                    minVal: componentValue.min,
                    maxVal: componentValue.max,
                };
            }
            else if (componentValue.min !== null) {
                value = {
                    type: 'minimum',
                    characteristic,
                    value: componentValue.min,
                };
            }
            else {
                value = {
                    type: 'maximum',
                    characteristic,
                    value: componentValue.max,
                };
            }
            return {
                name: `Connection through ${model}`,
                model,
                value,
                withConnections: [targetConnection],
            };
        }
    }
    logger.warn('ConvertFlatSignalTieToExternalComponent_UnsupportedConnectionType', {
        connectionType: flatTie.connectionType,
    });
    throw new Error(`Unsupported connection type: ${flatTie.connectionType}`);
}
function formatTieVarianceString(value, unitShort) {
    if (value.variance === null || value.varianceType === null) {
        return '';
    }
    if (value.varianceType === signal_tie_types_1.SignalTieVarianceType.Relative) {
        return `${value.variance}%`;
    }
    return `${sigma_engine_shared_1.Utils.formatNumericalSIValue(value.variance)}${unitShort}`;
}
function flatTieToRowValues(flatTie) {
    const empty = {
        componentType: '',
        componentValueAbs: '',
        componentValueMin: '',
        componentValueMax: '',
        componentValueVariance: '',
        polarity: '',
    };
    if (flatTie.connectionType === signal_tie_types_1.SignalTieConnectionType.Direct || flatTie.componentType === null) {
        return empty;
    }
    const units = signal_tie_types_1.TIE_COMPONENT_TYPE_TO_UNITS[flatTie.componentType];
    const withType = {
        ...empty,
        componentType: flatTie.componentType,
        polarity: (0, signal_tie_types_1.isPolarizedTieComponentType)(flatTie.componentType) ? (flatTie.polarity ?? '') : '',
    };
    const value = flatTie.componentValue;
    if (value === null) {
        return withType;
    }
    if (value.type === 'absolute') {
        return {
            ...withType,
            componentValueAbs: `${sigma_engine_shared_1.Utils.formatNumericalSIValue(value.value)}${units.short}`,
            componentValueVariance: formatTieVarianceString(value, units.short),
        };
    }
    return {
        ...withType,
        componentValueMin: value.min === null ? '' : `${sigma_engine_shared_1.Utils.formatNumericalSIValue(value.min)}${units.short}`,
        componentValueMax: value.max === null ? '' : `${sigma_engine_shared_1.Utils.formatNumericalSIValue(value.max)}${units.short}`,
    };
}
function signalTieRowValuesToFlatTie(args, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
    if (args.connectionType === signal_tie_types_1.SignalTieConnectionType.Direct) {
        return {
            toSignalId: args.toSignalId,
            connectionType: signal_tie_types_1.SignalTieConnectionType.Direct,
            componentType: null,
            componentValue: null,
        };
    }
    const componentType = args.componentType === '' ? null : args.componentType;
    if (componentType === null) {
        logger.warn('SignalTieRowValuesToFlatTie_MissingComponentType', { args });
        return null;
    }
    const abs = args.componentValueAbs.trim();
    const min = args.componentValueMin.trim();
    const max = args.componentValueMax.trim();
    const withoutValue = {
        toSignalId: args.toSignalId,
        connectionType: signal_tie_types_1.SignalTieConnectionType.ThroughComponent,
        componentType,
        componentValue: null,
        ...((0, signal_tie_types_1.isPolarizedTieComponentType)(componentType) && args.polarity !== '' && { polarity: args.polarity }),
    };
    if (abs !== '') {
        const value = tie_component_value_1.TieComponentValue.parse(abs, componentType);
        if (value === undefined) {
            logger.warn('SignalTieRowValuesToFlatTie_UnreadableValueCleared', { componentType, componentValueAbs: abs });
            return withoutValue;
        }
        const variance = tie_component_value_1.TieComponentValue.parseVariance(args.componentValueVariance, componentType);
        if (variance === undefined && args.componentValueVariance.trim() !== '') {
            logger.warn('SignalTieRowValuesToFlatTie_UnreadableVarianceCleared', {
                componentType,
                componentValueVariance: args.componentValueVariance,
            });
        }
        return {
            ...withoutValue,
            componentValue: {
                type: 'absolute',
                value,
                variance: variance?.variance ?? null,
                varianceType: variance?.varianceType ?? null,
            },
        };
    }
    if (min !== '' || max !== '') {
        const parsedMin = min === '' ? null : tie_component_value_1.TieComponentValue.parse(min, componentType);
        const parsedMax = max === '' ? null : tie_component_value_1.TieComponentValue.parse(max, componentType);
        if (parsedMin === undefined || parsedMax === undefined) {
            logger.warn('SignalTieRowValuesToFlatTie_UnreadableValueCleared', {
                componentType,
                componentValueMin: min,
                componentValueMax: max,
            });
            return withoutValue;
        }
        return { ...withoutValue, componentValue: { type: 'range', min: parsedMin, max: parsedMax } };
    }
    return withoutValue;
}
function applySignalTieRowsToSignals(signals, tieRows, logger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)()) {
    const rowsByName = new Map();
    const availableSignalNames = signals.map((signal) => signal.name);
    for (const entry of tieRows) {
        if (!signals.some((signal) => signal.name === entry.signalName)) {
            logger.warn('ApplySignalTieRowsToSignals_SignalNotFound', {
                signalName: entry.signalName,
                availableSignalNames,
            });
            continue;
        }
        rowsByName.set(entry.signalName, entry.ties);
    }
    return signals.map((signal) => {
        const rows = rowsByName.get(signal.name);
        if (rows === undefined) {
            return signal;
        }
        const externalComponents = [];
        for (const row of rows) {
            const toSignalId = sigma_engine_shared_1.DesignModels.findSignalIdBySignalName(row.toSignalName, signals, logger);
            if (!toSignalId) {
                logger.warn('ApplySignalTieRowsToSignals_ToSignalNotFound', {
                    toSignalName: row.toSignalName,
                    availableSignalNames,
                });
                continue;
            }
            const flatTie = signalTieRowValuesToFlatTie({
                toSignalId,
                connectionType: row.connectionType,
                componentType: row.componentType,
                componentValueAbs: row.componentValueAbs,
                componentValueMin: row.componentValueMin,
                componentValueMax: row.componentValueMax,
                componentValueVariance: row.componentValueVariance,
                polarity: row.polarity,
            }, logger);
            if (flatTie === null) {
                continue;
            }
            try {
                externalComponents.push(convertFlatSignalTieToExternalComponent(flatTie, signals, logger));
            }
            catch (error) {
                logger.warn('ApplySignalTieRowsToSignals_ConversionFailed', {
                    signalName: signal.name,
                    error: error instanceof Error ? error.message : String(error),
                });
            }
        }
        return {
            ...signal,
            ties: externalComponents.length > 0 ? externalComponents : undefined,
        };
    });
}
function splitExternalComponentByConnection(component) {
    const connections = component.withConnections;
    if (component.model === undefined || connections === undefined || connections.length <= 1) {
        return [component];
    }
    return connections.map((connection) => ({ ...component, withConnections: [connection] }));
}
function normalizeSignalTies(signals) {
    return signals.map((signal) => signal.ties && signal.ties.length > 0 ?
        { ...signal, ties: signal.ties.flatMap(splitExternalComponentByConnection) }
        : signal);
}
//# sourceMappingURL=signal-tie-transforms.js.map